import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Landmark, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHead } from "@/pages/admin/AdminLayout";
import { api, gbp, formatApiError, longDate } from "@/lib/api";

const STATUS = {
  none: ["Stripe not connected", "Connect your own Stripe account (new or existing). Patients pay you directly, and Stripe's card fees come from your account. Your clinic is hidden from patients until it's connected."],
  onboarding: ["Connection not finished", "Finish the Stripe form to start taking payments."],
  pending: ["Being verified by Stripe", "Stripe is checking your details. This usually takes a few minutes to a day."],
  active: ["Stripe connected", "Patients pay straight into your Stripe account. Stripe pays out to your bank on your Stripe payout schedule."],
};

const Kpi = ({ label, value, testid, hint }) => (
  <div className="rounded-2xl bg-card border border-border p-5" data-testid={testid}>
    <p className="text-sm text-muted-foreground">{label}</p>
    <p className="font-display text-2xl font-extrabold mt-1">{value}</p>
    {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
  </div>
);

export default function Payouts() {
  const [params, setParams] = useSearchParams();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/payouts").then((r) => { setD(r.data); window.dispatchEvent(new Event("clinic-updated")); });
  useEffect(() => {
    load();
    if (params.get("return")) { toast.success("Thanks — checking your Stripe connection"); setParams({}); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/payouts/onboard", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(formatApiError(e)); setBusy(false); }
  };

  if (!d) return <Loader2 className="animate-spin text-primary" data-testid="payouts-loading" />;
  const [title, text] = STATUS[d.connect_status] || STATUS.none;
  const s = d.summary;

  return (
    <div className="fade-up">
      <PageHead eyebrow="Money" title="Payments & Stripe" />
      <div className="rounded-2xl bg-card border border-border p-6 flex flex-wrap items-center justify-between gap-4 mb-6" data-testid="payouts-status-card">
        <div className="flex gap-4 items-start">
          <span className="h-11 w-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center"><Landmark className="h-5 w-5" /></span>
          <div>
            <p className="font-display font-bold text-lg" data-testid="payouts-status">{title}</p>
            <p className="text-sm text-muted-foreground max-w-xl">{d.sample && d.connect_status !== "active" ? "Sample clinic: test payments currently go to the platform's test account." : text}</p>
            {d.requirements_due > 0 && d.connect_status !== "none" && <p className="text-sm text-amber-700 mt-1">Stripe needs {d.requirements_due} more detail{d.requirements_due > 1 ? "s" : ""} from you.</p>}
          </div>
        </div>
        <div className="flex gap-2">
          {d.connect_status !== "active" && (
            <Button className="rounded-full" disabled={busy} onClick={connect} data-testid="payouts-onboard-button">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : d.connect_status === "none" ? "Connect Stripe" : "Continue setup"}
            </Button>
          )}
          {d.details_submitted && (
            <Button variant="outline" className="rounded-full" asChild data-testid="payouts-dashboard-button">
              <a href="https://dashboard.stripe.com" target="_blank" rel="noreferrer">Open Stripe <ExternalLink className="h-3.5 w-3.5 ml-1.5" /></a>
            </Button>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="Booking payments" value={gbp(s.gross)} testid="payouts-gross" hint={`${s.bookings} paid booking${s.bookings === 1 ? "" : "s"}`} />
        <Kpi label="Platform fees (total)" value={gbp(s.fees)} testid="payouts-fees" hint="Billed separately, never taken from payments" />
        <Kpi label="Fees added to your plan invoice" value={gbp(s.fees_billed)} testid="payouts-fees-billed" />
        <Kpi label="Fees not yet invoiced" value={gbp(s.fees_unbilled)} testid="payouts-fees-unbilled" hint={d.has_subscription ? "Added to your next monthly plan invoice" : ""} />
      </div>
      {d.balance && (
        <div className="grid grid-cols-2 gap-4 mt-4">
          <Kpi label="Stripe balance available" value={gbp(d.balance.available)} testid="payouts-balance-available" />
          <Kpi label="Stripe balance pending" value={gbp(d.balance.pending)} testid="payouts-balance-pending" />
        </div>
      )}
      {d.payouts.length > 0 && (
        <div className="rounded-2xl bg-card border border-border p-5 mt-6" data-testid="payouts-history">
          <p className="font-display font-bold mb-2">Recent bank payouts</p>
          {d.payouts.map((p) => (
            <div key={p.id} className="flex justify-between py-2 border-b border-border last:border-0 text-sm">
              <span>{longDate(p.arrival_date)}</span><span className="capitalize text-muted-foreground">{p.status.replace("_", " ")}</span><span className="font-semibold">{gbp(p.amount)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
